import Alpine from 'alpinejs';
import lottie from 'lottie-web';

const DEFAULT_OPTIONS = {
    loop: true,
    autoplay: true,
    renderer: 'svg',
    playOnView: false,
    threshold: 0.2,
    rootMargin: '0px',
};

function buildAnimationConfig(container, options, autoplay) {
    const config = {
        container,
        renderer: options.renderer,
        loop: options.loop,
        autoplay,
    };

    if (options.animationData) {
        config.animationData = options.animationData;
    } else if (options.path) {
        config.path = options.path;
    }

    return config;
}

/**
 * Create a Lottie animation instance.
 *
 * @param {HTMLElement} container
 * @param {object} options
 * @returns {import('lottie-web').AnimationItem|null}
 */
export function createLottieAnimation(container, options = {}) {
    const merged = { ...DEFAULT_OPTIONS, ...options };

    if (!merged.path && !merged.animationData) {
        console.warn('lottie: missing path or animationData', container);
        return null;
    }

    return lottie.loadAnimation(
        buildAnimationConfig(container, merged, merged.autoplay)
    );
}

/**
 * Alpine.js data component for Lottie animations.
 *
 * Usage in PHP templates:
 *
 * <div
 *   class="my-component__animation"
 *   x-data="lottie(<?php echo htmlspecialchars(json_encode([
 *     'path' => get_template_directory_uri() . '/animations/example.json',
 *     'loop' => true,
 *     'autoplay' => false,
 *     'playOnView' => true,
 *     'threshold' => 0.5,
 *     'rootMargin' => '0px 0px -50% 0px',
 *   ]), ENT_QUOTES, 'UTF-8'); ?>)"
 * ></div>
 */
Alpine.data('lottie', (initialOptions = {}) => ({
    animation: null,
    observer: null,
    options: { ...DEFAULT_OPTIONS, ...initialOptions },

    init() {
        const container = this.$refs.container || this.$el;

        if (!this.options.path && !this.options.animationData) {
            console.warn('lottie: missing path or animationData', container);
            return;
        }

        if (this.options.playOnView) {
            this.initPlayOnView(container);
            return;
        }

        this.loadAnimation(container);
    },

    destroy() {
        if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
        }

        this.destroyAnimation();
    },

    initPlayOnView(container) {
        this.observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) {
                    return;
                }

                if (!this.animation) {
                    this.loadAnimation(container, true);
                } else {
                    this.animation.play();
                }

                this.observer?.disconnect();
                this.observer = null;
            });
        }, {
            threshold: this.options.threshold,
            rootMargin: this.options.rootMargin,
        });

        this.observer.observe(this.$el);
    },

    loadAnimation(container, autoplay = this.options.autoplay) {
        this.destroyAnimation();
        this.animation = createLottieAnimation(container, {
            ...this.options,
            autoplay,
        });
    },

    destroyAnimation() {
        if (this.animation) {
            this.animation.destroy();
            this.animation = null;
        }
    },

    play() {
        this.animation?.play();
    },

    pause() {
        this.animation?.pause();
    },

    stop() {
        this.animation?.stop();
    },
}));
